import { useEffect, useMemo, useRef } from 'react'
import { AlertTriangle, Check, Loader2 } from 'lucide-react'
import { StepShell } from '@/app/components/ui/StepShell'
import { readPhoto } from '@/app/lib/local/watcher'
import { layoutBackground, layoutOverlay, listLayouts } from '@/app/lib/local/layouts'
import { renderQr } from '@/app/lib/qr'
import { cn } from '@/app/lib/cn'
import { resolveSelected, useAppStore } from '@/app/store/useAppStore'
import { useFlowStore, type RenderedLayout } from '@/app/store/useFlowStore'
import { composite } from '@/core/composite'
import { decodeImage, type DecodedImage } from '@/core/exif'
import { shareUrlFor } from '@/core/session'

const FRONTEND_BASE = import.meta.env.VITE_FRONTEND_BASE ?? 'https://raiaphoto.tech'

/**
 * Step 2 — choose the layout.
 *
 * Every layout is composited with the operator's actual photos and the real QR,
 * so what they pick from is what will come out of the printer. The desktop app
 * showed abstract preview thumbnails at configuration time and only rendered
 * the real thing on the print screen, which meant slot-mismatch surprises were
 * discovered after committing.
 *
 * Mismatches are surfaced on the card itself rather than left to be noticed: a
 * photo whose orientation does not match its slot is silently dropped by the
 * compositor, and an operator has no way to infer that from a thumbnail.
 */
export function LayoutStep({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const store = useAppStore()
  const selected = useFlowStore((s) => s.selected)
  const files = useMemo(() => resolveSelected(store.files, selected), [store.files, selected])

  const {
    sessionKey,
    beginSession,
    rendered,
    setRendered,
    renderError,
    setRenderError,
    chosenLayoutId,
    chooseLayout,
  } = useFlowStore()

  const startedFor = useRef<string | null>(null)

  useEffect(() => {
    // `beginSession` is idempotent and the key is normally minted when the
    // operator leaves step 1, so this is a fallback for a direct mount.
    //
    // It used to be the ONLY place it was minted, which made this effect
    // trigger itself: it set `sessionKey`, `sessionKey` is the dependency, so
    // the effect re-ran and composited the whole layout set a second time.
    // Every photo decoded twice and every background composited twice, per
    // guest, while one stood waiting.
    const key = beginSession()
    // Compositing is expensive; do it once per session, not once per mount.
    if (startedFor.current === key && rendered) return
    startedFor.current = key

    let cancelled = false
    const urls: string[] = []

    ;(async () => {
      try {
        setRenderError(null)
        const layouts = await listLayouts()
        if (layouts.length === 0) {
          setRenderError('Belum ada layout. Buat dulu di Pengaturan.')
          return
        }

        const images: DecodedImage[] = []
        for (const file of files) {
          const source = await readPhoto(file)
          // The operator moved or deleted it between choosing and getting here.
          // Dropping it keeps the remaining photos printable; failing the step
          // would strand a guest over a file nobody can get back.
          if (!source) continue
          images.push(await decodeImage(source))
        }

        const qr = await createImageBitmap(await renderQr(shareUrlFor(key, FRONTEND_BASE), 240))

        const out: RenderedLayout[] = []
        for (const layout of layouts) {
          try {
            const blob = await layoutBackground(layout)
            if (!blob) continue
            const background = await createImageBitmap(blob)

            // Drawn over the photos, under the QR — see core/composite.ts.
            const overlayBlob = await layoutOverlay(layout)
            const overlay = overlayBlob ? await createImageBitmap(overlayBlob) : null

            const result = await composite(
              { layout, background, images, overlay, qr: layout.qr ? qr : null },
              { archiveWithoutQr: store.settings?.archiveWithoutQr ?? false },
            )
            const previewUrl = URL.createObjectURL(result.print)
            urls.push(previewUrl)
            out.push({ layout, result, previewUrl })
          } catch {
            // One unreadable background must not take the whole step down.
          }
        }

        if (cancelled) {
          urls.forEach(URL.revokeObjectURL)
          return
        }
        if (out.length === 0) {
          setRenderError('Tidak ada layout yang bisa dirender. Cek gambar latar layout.')
          return
        }

        setRendered(out)
        // Preselect the layout that wastes nothing.
        const clean = out.find((r) => r.result.emptySlots.length === 0 && r.result.droppedImages.length === 0)
        chooseLayout((clean ?? out[0]!).layout.id)
      } catch (error) {
        if (!cancelled) {
          setRenderError(error instanceof Error ? error.message : 'Gagal membuat pratinjau')
        }
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey])

  const chosen = rendered?.find((r) => r.layout.id === chosenLayoutId)

  return (
    <StepShell
      title="Pilih layout"
      hint="Pratinjau ini persis seperti hasil cetaknya."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!chosen}
      status={
        chosen && (chosen.result.emptySlots.length > 0 || chosen.result.droppedImages.length > 0)
          ? 'Layout ini tidak memakai semua foto'
          : undefined
      }
    >
      {renderError && (
        <div className="rounded-[14px] bg-warn/10 p-5 text-[15px] text-warn ring-1 ring-warn/30">
          {renderError}
        </div>
      )}

      {!rendered && !renderError && (
        <div className="grid place-items-center py-24 text-center text-ink-muted">
          <Loader2 size={32} className="mb-4 animate-spin text-brand" />
          Menyiapkan pratinjau…
        </div>
      )}

      {rendered && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}
        >
          {rendered.map((item) => {
            const active = item.layout.id === chosenLayoutId
            const unused = item.result.emptySlots.length + item.result.droppedImages.length

            return (
              <button
                key={item.layout.id}
                type="button"
                onClick={() => chooseLayout(item.layout.id)}
                aria-pressed={active}
                className={cn(
                  'group relative overflow-hidden rounded-[16px] bg-raised p-3 text-left',
                  'ring-1 transition-transform active:scale-[.99]',
                  active ? 'ring-2 ring-brand' : 'ring-line',
                )}
              >
                <div className="grid aspect-[2/3] place-items-center overflow-hidden rounded-[10px] bg-black/30">
                  <img
                    src={item.previewUrl}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                    draggable={false}
                  />
                </div>

                <div className="mt-3 flex min-h-[24px] items-center gap-2">
                  {active && (
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand text-on-brand">
                      <Check size={14} strokeWidth={3} />
                    </span>
                  )}
                  <span className="truncate text-[14px] text-ink-muted">
                    {item.layout.slots.length} slot
                  </span>
                  {unused > 0 && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[13px] text-warn">
                      <AlertTriangle size={14} />
                      {unused}
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}

      {chosen && chosen.result.emptySlots.length > 0 && (
        <p className="mt-4 rounded-[12px] bg-warn/10 p-4 text-[14px] text-warn ring-1 ring-warn/25">
          {chosen.result.emptySlots.length} slot akan kosong — orientasi foto tidak cocok dengan
          slotnya. Pilih layout lain atau ganti fotonya.
        </p>
      )}
      {chosen && chosen.result.droppedImages.length > 0 && (
        <p className="mt-3 rounded-[12px] bg-warn/10 p-4 text-[14px] text-warn ring-1 ring-warn/25">
          {chosen.result.droppedImages.length} foto tidak punya slot di layout ini dan tidak akan
          dicetak.
        </p>
      )}
    </StepShell>
  )
}
